/**
 * Number rendering for every figure in the product.
 *
 * Its own module rather than a export from `<MetricCell>` because `<Breakdown>` needs the same
 * rules and `<MetricCell>` imports `<Breakdown>` — a cycle between the two would be a runtime
 * hazard for what is, in the end, arithmetic. Both import this instead.
 *
 * ## Compact above 1,000 to two decimals, exact below it (owner decision, 2026-08-30)
 *
 * Was 10,000 to one decimal. Both halves changed together, and the effect is not what "compact"
 * usually buys — **it is not shorter**. At two decimal places the compacted form is the same glyph
 * count as the exact figure at every magnitude the product carries:
 *
 * | exact     | compacted |
 * | --------- | --------- |
 * | `1,234`   | `1.23K`   |
 * | `5,269`   | `5.27K`   |
 * | `20,135`  | `20.14K`  |
 * | `308,696` | `308.70K` |
 *
 * So this is a **reading** choice, not a width one: every figure over three digits now presents as
 * a magnitude rather than as a count, uniformly. Worth knowing before anyone "optimises" the
 * decimals back down to save space they were never spending.
 *
 * ⚠ **One surface prints one decimal**: `formatNumberNarrow`, for the chart card's matrix view
 * (owner request 2026-09-10), where thirteen `1fr` columns make a glyph cost thirteen glyphs
 * across a row. That is a genuine width decision and it is deliberately scoped to that one table
 * — see the note on `compactNarrow`.
 *
 * The measurement that set the old threshold still stands and still argues the other way — in the
 * table *body* the largest value anywhere is 10,986, only 2 of 10,152 rows reach five figures, and
 * GM tops out at **693** — so the previous rule left essentially every scanned cell an exact count.
 * That was the trade the owner reversed, knowingly. It is recorded rather than deleted because a
 * future reader looking at `1.00K` where `1,000` would fit deserves to know it was asked for.
 *
 * **Nothing becomes unreachable.** A compacted figure keeps its exact value in the hover/focus
 * panel and in its accessible name — `<MetricCell>` and the KPI strip both route through
 * `<Disclosure>`, so the precise number is one hover or one `Tab` away (FR-062). Compaction is a
 * display rule, never a loss of data.
 *
 * FR-055 outranks all of it: a value that cannot be computed is `—`, never `0` and never blank.
 */

export const EM_DASH = '—';

/**
 * Below this, render the exact figure. One constant on purpose — every figure in the product goes
 * through `formatNumber`, so this is the whole threshold.
 *
 * At 1,000 essentially every table cell above three digits compacts, which is the point. The
 * consequence to keep in mind is `isCompacted()`: it now returns true for far more figures, and
 * `<MetricCell>` and the KPI strip attach a `<Disclosure>` only where it does. More cells gain a
 * hover panel — that is correct (the exact figure must stay reachable) but it is also more DOM.
 */
export const COMPACT_FROM = 1_000;

const exact = new Intl.NumberFormat('en-US');
/**
 * `minimumFractionDigits` as well as `maximumFractionDigits` — the owner asked for two digits after
 * the point, always. Without the minimum, Intl trims trailing zeros and the column becomes ragged:
 * `8.7K` beside `20.14K` beside `1K`.
 *
 * It is only ever applied at or above `COMPACT_FROM`. Were that ever lowered toward zero, the
 * minimum would start printing `693.00` for three-figure values — the guard is the threshold in
 * `formatNumber`, not this formatter.
 */
const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * The same rule at ONE decimal — the chart card's matrix view alone (owner request 2026-09-10).
 *
 * ⚠ **This is the one place in the product that does not print two**, and it is a width decision
 * where §6.1.1's is a reading decision. Every other figure sits in a table with a dozen columns at
 * most; the matrix carries **twelve bucket columns plus a total**, all of them `1fr` and therefore
 * all of them at their minimum on any laptop, so a glyph in a cell there costs a glyph thirteen
 * times across the row. `20.14K` → `20.1K` is 6.9px back per column at IBM Plex Mono's fixed
 * 600/1000-em advance.
 *
 * The `minimumFractionDigits` is here for the reason it is on `compact` above — without it Intl
 * trims the trailing zero and a column goes ragged (`8.7K` beside `20.1K` beside `1K`).
 *
 * ⚠ **Nothing is lost, and the panel is where it stays**: `EXACT_OF` maps this to
 * `formatNumberExact`, so a matrix cell's hover panel and its accessible name carry every digit
 * exactly as a two-decimal cell's do (FR-062). Widening the use of this beyond that one table is
 * the change to refuse — §6.1.1 is uniform across the product on purpose, and a page mixing
 * `20.1K` with `20.14K` reads as two different quantities.
 */
const compactNarrow = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const isBlank = (value: number | null | undefined): value is null | undefined =>
  value === null || value === undefined || Number.isNaN(value);

/** `9.36K` above the threshold, `693` below it. */
export function formatNumber(value: number): string {
  return Math.abs(value) >= COMPACT_FROM ? compact.format(value) : exact.format(value);
}

/**
 * `9.4K` above the threshold, `693` below it — `formatNumber`'s one-decimal twin, for the chart
 * matrix and nothing else. Passed as `formatValue`'s `format` argument, so a null still renders
 * `—` (FR-055) rather than being formatted into something.
 */
export function formatNumberNarrow(value: number): string {
  return Math.abs(value) >= COMPACT_FROM ? compactNarrow.format(value) : exact.format(value);
}

/** Always the full figure — what the tooltip and the accessible name carry. */
export function formatNumberExact(value: number): string {
  return exact.format(value);
}

/**
 * The display figure. `format` is an escape hatch for columns with their own unit (money,
 * percentages); it is trusted to have already decided how to compact.
 */
export function formatValue(
  value: number | null | undefined,
  format?: (v: number) => string,
): string {
  if (isBlank(value)) return EM_DASH;
  return format ? format(value) : formatNumber(value);
}

/**
 * The same figure, uncompacted, for the panel and the accessible name.
 *
 * `format` is honoured so a money column announces `$12,345` rather than a bare `12345` — its
 * exact twin is `moneyExact`, paired through `EXACT_OF` below.
 */
export function formatValueExact(
  value: number | null | undefined,
  format?: (v: number) => string,
): string {
  if (isBlank(value)) return EM_DASH;
  if (!format) return formatNumberExact(value);
  return (EXACT_OF.get(format) ?? format)(value);
}

/** `$693`, `$12.35K`, `$1.50M` — the same threshold, so money and counts compact together. */
export function money(value: number): string {
  return `$${formatNumber(Math.round(value))}`;
}

/** `$693`, `$12,345` — money with every digit, for panels and accessible names. */
export function moneyExact(value: number): string {
  return `$${formatNumberExact(Math.round(value))}`;
}

/**
 * The uncompacted twin of a unit formatter.
 *
 * A registry rather than a second argument at every call site: `format` is passed down through
 * `<DataTable>` column specs, and threading a parallel `formatExact` beside each one would mean
 * every column declaring the same fact twice — and silently losing the exact figure wherever
 * someone forgot.
 */
const EXACT_OF = new Map<(v: number) => string, (v: number) => string>([
  [money, moneyExact],
  // Without this entry `formatValueExact` would fall back to calling the formatter it was handed,
  // and every matrix panel would repeat the compacted face it was opened to see behind.
  [formatNumberNarrow, formatNumberExact],
]);

/** Whether the display figure hides digits — i.e. whether the panel is saying something new. */
export function isCompacted(
  value: number | null | undefined,
  format?: (v: number) => string,
): boolean {
  if (isBlank(value)) return false;
  return formatValue(value, format) !== formatValueExact(value, format);
}
